import { cn, Icon, LinkButton, Separator, Skeleton, useOnline } from "@iec-id/ui";
import type { ReactNode } from "react";
import { SupportLink } from "../support/support-link";
import { BrandMark } from "./brand-mark";
import { LocaleSwitcher } from "./locale-switcher";
import { useSigninT } from "./signin-i18n";

/**
 * ── РИТМ ОТСТУПОВ ЭКРАНОВ ВХОДА И МАСТЕРА ──────────────────────────────────
 *
 * Закон близости: расстояние сообщает о родстве, поэтому шкала всего три шага
 * и она одна на вход, мастер, согласия и правовой центр.
 *
 *   24 (`gap-6`)   — между СЕКЦИЯМИ карточки: навигация · заголовок · тело ·
 *                    действие · подвал. Это «внешний» зазор.
 *   20 (`gap-5`)   — от группы к её действию: поля формы → «Продолжить».
 *   12 (`gap-3`)   — ВНУТРИ группы однородных элементов: поля между собой,
 *                    строки перечисления.
 *   10 и меньше    — внутри одного элемента: метка ↔ значение, знак ↔ подпись.
 *
 * Пока всё стояло на 20, ритма не было вовсе: поле и соседнее поле отстояли
 * ровно так же, как форма от подвала, — и глаз не мог отделить группу от
 * группы.
 */

/**
 * Оболочка всех экранов входа: 400px, r12, белая, паддинг 32, секционный зазор
 * 24 (макет 4a, `--layout-card-w-auth` / `--layout-card-pad-auth`; шкала — в
 * шапке файла).
 *
 * На 360px карточка занимает всю доступную ширину, а паддинг ужимается до 24 —
 * иначе шести ячейкам кода остаётся меньше 40px на ячейку и код перестаёт
 * читаться.
 */
export function AuthCard({
	children,
	className,
	brand = true,
	footer,
}: {
	children: ReactNode;
	className?: string;
	/**
	 * Подвал ПОД карточкой — сейчас там ссылка «Поддержка».
	 *
	 * Живёт в общей оболочке по той же причине, что и шапка: пока каждый экран
	 * звал бы её руками, ссылки не было бы ровно там, где она нужнее всего —
	 * на вводе кода и на экране ошибки входа.
	 */
	footer?: ReactNode | null;
	/**
	 * Шапка со знаком IEC ID и выбором языка. Выключать её незачем — параметр
	 * оставлен на случай экрана, у которого шапка своя, и по умолчанию она
	 * ВКЛЮЧЕНА сознательно: пока каждый экран звал `AuthBrandBar` руками, знака
	 * не было на вводе кода, на согласиях и на экране ошибки — то есть логотип
	 * пропадал ровно посреди входа. Общая оболочка забыть его не может.
	 */
	brand?: boolean;
}) {
	// Знак и выбор языка вынесены НАД карточкой и разведены по её краям — так
	// же, как на лендинге приглашения (решение владельца 22.08.2026). Белая
	// карточка остаётся ровно формой входа, а «куда я попал» и «на каком языке»
	// живут на канве над ней.
	return (
		<div className="flex w-full max-w-[400px] flex-col gap-4">
			{brand ? <AuthBrandBar /> : null}
			<main className={cn("flex flex-col gap-6 rounded-card bg-card p-6 sm:p-8", className)}>
				{children}
			</main>
			{/*
			 * Подвал по умолчанию — ссылка «Поддержка». Явный `null` её
			 * убирает: правовому центру она не нужна, там своя навигация.
			 */}
			{footer === null ? null : (
				<div className="flex justify-center">{footer ?? <SupportLink />}</div>
			)}
		</div>
	);
}

/**
 * Шапка НАД карточкой: знак + слово «IEC ID» слева, выбор языка справа.
 *
 * Название сервиса живёт здесь, а не в заголовке экрана. Раньше «IEC ID»
 * встречалось трижды подряд — в знаке, в «Войти в IEC ID» и в «Ваш IEC ID —
 * один аккаунт…»; читателю это ничего не добавляло, а первую строку карточки
 * занимало. Теперь шапка отвечает на «куда я попал», заголовок — на «что здесь
 * делать».
 *
 * Знак 48px и вордмарк `t-wordmark` (16px Geist Mono) — как в шапке витрины
 * и в брифe (48px mark, зазор 11px). Раньше здесь стояли 40px и `text-caps`
 * 12px, и пара пропадала на фоне заголовка экрана.
 * Слово не ссылка: уходить с экрана входа некуда.
 */
export function AuthBrandBar({ wordmark = true }: { wordmark?: boolean }) {
	const t = useSigninT();
	return (
		<div className="flex items-center justify-between gap-3">
			<div className="flex items-center gap-[var(--sp-11)]">
				<BrandMark title={wordmark ? undefined : t("brand.alt")} />
				{wordmark ? <span className="t-wordmark text-fg">{t("brand.wordmark")}</span> : null}
			</div>
			<LocaleSwitcher />
		</div>
	);
}

/**
 * Заголовок с подписью. Жирного в системе нет: заголовок отличается только
 * размером (24px) и тем, что подпись уходит в приглушённый цвет.
 */
export function AuthHeading({
	title,
	subtitle,
	badge,
}: {
	title: ReactNode;
	subtitle?: ReactNode;
	/**
	 * Знак сервиса, в который человек входит, — слева от заголовка, на одной с
	 * ним линии. Источник знака ТОЛЬКО реестр, не параметр запроса: почему
	 * именно так — в `service-badge.tsx`.
	 */
	badge?: ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			{badge != null ? (
				// `items-center` по первой строке заголовка: у длинного имени
				// сервиса (немецкий, венгерский) заголовок переносится, и знак
				// обязан остаться у первой строки, а не уехать в середину блока.
				<div className="flex items-center gap-2.5">
					{badge}
					<h1 className="t-section min-w-0 break-words">{title}</h1>
				</div>
			) : (
				<h1 className="t-section">{title}</h1>
			)}
			{subtitle != null ? <p className="t-body text-fg-muted">{subtitle}</p> : null}
		</div>
	);
}

/** Разделитель «или» между входом через Google и входом по коду. */
export function OrDivider({ label }: { label: string }) {
	return (
		<div className="flex items-center gap-3 t-meta text-fg-muted">
			<Separator className="flex-1" />
			<span>{label}</span>
			<Separator className="flex-1" />
		</div>
	);
}

/** «Назад» со стрелкой — первая строка экрана кода (макет 4a, карточка 2). */
export function BackLink({ onClick }: { onClick: () => void }) {
	const t = useSigninT();
	return (
		<LinkButton className="gap-2.5 text-fg-muted" onClick={onClick}>
			<Icon name="ArrowLeft" size={20} />
			{t("common.back")}
		</LinkButton>
	);
}

/**
 * Предупреждение об отсутствии сети.
 *
 * Стоит над формой, а не вместо неё: поле остаётся заполняемым, чтобы человек
 * не терял набранное, пока сеть возвращается. `aria-live` — потому что
 * появление баннера не сопровождается перемещением фокуса.
 */
export function OfflineNotice() {
	const t = useSigninT();
	const online = useOnline();
	if (online) return null;

	return (
		<div
			role="status"
			aria-live="polite"
			className="flex items-start gap-2.5 rounded-control bg-danger-soft px-4 py-3"
		>
			<span className="mt-0.5 shrink-0 text-danger">
				<Icon name="TriangleAlert" size={16} />
			</span>
			<span className="flex flex-col gap-0.5">
				<span className="t-body">{t("offline.title")}</span>
				<span className="t-meta text-fg-muted">{t("offline.description")}</span>
			</span>
		</div>
	);
}

/**
 * Пока идёт тихий запрос сессии, карточка ПОВТОРЯЕТ будущий экран, а не
 * показывает спиннер по центру.
 *
 * Спиннер с подписью стоил двух неприятностей сразу. Во-первых, высота
 * (`min-h-[24rem]`) была выбрана на глаз и не совпадала с формой: карточка
 * подпрыгивала на ~130px ровно в момент, когда человек начинал её читать.
 * Во-вторых, экран менялся дважды — «идёт проверка» и следом «войти», — хотя
 * человек ждёт одного: формы входа.
 *
 * Скелет собран из тех же блоков и тех же высот, что `EmailStep` (заголовок с
 * подписью, кнопка 64, разделитель, поле 64, секционный зазор 24), поэтому переход к
 * готовому экрану не двигает ни карточку, ни то, что под ней. Подпись
 * осталась для скринридера: ему пустые плашки не сообщают ничего.
 *
 * LCP при этом не пострадал — ради него высота и фиксировалась: крупнейший
 * элемент по-прежнему появляется на первом кадре SPA, а не после ответа API.
 */
export function AuthPending({ label }: { label: string }) {
	return (
		<AuthCard>
			<div role="status" aria-live="polite" className="flex flex-col gap-6">
				<span className="sr-only">{label}</span>
				<div className="flex flex-col gap-1.5" aria-hidden="true">
					<Skeleton height={29} width="60%" radius="chip" />
					<Skeleton height={22} width="85%" radius="chip" />
				</div>
				{/* 64 — `min-h-16` кнопки кита; 68 — поле с подписью: 27 сверху,
				    22.5 строка `t-body`, 17 снизу и рамка (`GEOMETRY.md.labeled`
				    в `packages/ui/src/components/input.tsx`). Числа десктопные:
				    на coarse-указателе строка на пиксель выше, и это не видно. */}
				<Skeleton height={64} radius="card" />
				{/* Место разделителя «или» — пустое: линия с текстом в скелете
				    читается как готовый контент, которого ещё нет. */}
				<div className="h-5" aria-hidden="true" />
				<Skeleton height={68} radius="card" />
			</div>
		</AuthCard>
	);
}
